import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "danger" | "ghost";
type Size = "sm" | "md" | "lg" | "icon";

/**
 * Bouton d'action standard du DS (§6.2). Rend un `<button>` natif et accepte
 * tous ses attributs (`onClick`, `disabled`, `aria-*`…). L'anneau de focus est
 * toujours orange (§5.1) ; l'orange = la flamme/l'action reste un CONTOUR, jamais
 * un grand aplat (sauf `danger`).
 *
 * Quand l'utiliser : toute action ponctuelle (valider, annuler, supprimer…).
 * Pour un gros CTA d'accueil avec titre + sous-titre, préférer `ActionCard`.
 */
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Rôle visuel (défaut `primary`).
   * - `primary` : contour orange (l'action principale, l'étincelle).
   * - `secondary` : mauve doux (action de structure / alternative).
   * - `ghost` : transparent, discret (action tertiaire).
   * - `danger` : aplat rouge + blanc (action destructrice).
   */
  variant?: Variant;
  /**
   * Gabarit (défaut `md`). `sm`/`md`/`lg` dimensionnent padding + taille de
   * texte ; `icon` = cible tactile carrée 44×44 px pour un glyphe seul (penser
   * à fournir un `aria-label`).
   */
  size?: Size;
  /** Pleine largeur du conteneur (`w-full`). Défaut `false`. */
  block?: boolean;
  children: ReactNode;
}

// Variants alignés sur le DS Flambeau (§6.2) : orange = la flamme/l'action
// (rare, en étincelle, jamais en grand aplat) → le primaire est un CONTOUR
// orange ; mauve = la structure → le secondaire est un mauve doux.
const VARIANT: Record<Variant, string> = {
  primary:
    "border border-orange-deep bg-orange-soft text-orange-deep hover:border-orange hover:text-orange active:bg-orange-soft disabled:opacity-50",
  secondary:
    "border border-flambeau-200 bg-flambeau-50 text-flambeau-700 hover:bg-flambeau-100 active:bg-flambeau-200 disabled:opacity-50",
  ghost:
    "bg-transparent text-muted hover:bg-flambeau-50 active:bg-flambeau-100 disabled:opacity-50",
  // Danger = aplat rouge + blanc (paire AA via red-deep au survol).
  danger: "bg-red text-white hover:bg-red-deep active:bg-red-deep disabled:opacity-50",
};

const SIZE: Record<Size, string> = {
  sm: "px-3 py-1.5 text-small",
  md: "px-4 py-3 text-body",
  lg: "px-6 py-4 text-title-sm",
  // Icône-bouton : cible tactile carrée ≥ 44×44 px (un seul glyphe).
  icon: "h-11 w-11 p-0",
};

export function Button({
  variant = "primary",
  size = "md",
  block = false,
  className = "",
  type = "button",
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    "inline-flex items-center justify-center gap-2 rounded-md font-semibold",
    "transition-colors duration-150",
    // L'anneau de focus EST orange : la flamme marque ce qui est actif (§5.1).
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed",
    VARIANT[variant],
    SIZE[size],
    block ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type={type} className={classes} {...rest}>
      {children}
    </button>
  );
}
